import type useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import type { FilterConfig, FilterOption } from '@repo/ui/src/primitives/FilterChipDropdown';
import type { Facet } from '@hooks/search/useSearch';
import { SINGLE_VALUE_FILTER_KEYS } from '@hooks/search/useSearchUrlState';

// Static metadata: translation key, single-select flag, and toggle flag per facet key
const FACET_META: Record<
  string,
  { labelKey: string; labelDefault: string; singleSelect?: boolean; toggle?: boolean }
> = {
  // Product facets (priceGroup excluded — needs backend work)
  categories: { labelKey: 'search.facet.categories', labelDefault: 'Category' },
  grillSeries: { labelKey: 'search.facet.grillSeries', labelDefault: 'Grill Series' },
  color: { labelKey: 'search.facet.color', labelDefault: 'Color' },
  apparelFit: { labelKey: 'search.facet.apparelFit', labelDefault: 'Apparel Fit' },
  apparelSize: { labelKey: 'search.facet.apparelSize', labelDefault: 'Apparel Size' },
  features: { labelKey: 'search.facet.features', labelDefault: 'Features' },
  grillCompatibility: {
    labelKey: 'search.facet.grillCompatibility',
    labelDefault: 'Grill Compatibility',
  },
  pairings: { labelKey: 'search.facet.pairings', labelDefault: 'Pairings' },
  // Recipe facets
  mealType: { labelKey: 'search.facet.mealType', labelDefault: 'Meal Type' },
  hardwood: { labelKey: 'search.facet.hardwood', labelDefault: 'Hardwood' },
  holiday: { labelKey: 'search.facet.holiday', labelDefault: 'Holiday' },
  cookingMethod: { labelKey: 'search.facet.cookingMethod', labelDefault: 'Cooking Method' },
  relatedCategories: { labelKey: 'search.facet.relatedCategories', labelDefault: 'Category' },
  // Learn facets
  tags: { labelKey: 'search.facet.tags', labelDefault: 'Tags' },
  featuredContent: { labelKey: 'search.facet.featuredContent', labelDefault: 'Featured', toggle: true },
};

// Display order for filter chips (keys not listed here appear at the end)
const FACET_ORDER: Array<string> = [
  // Product
  'categories',
  'grillSeries',
  'color',
  'apparelFit',
  'apparelSize',
  'features',
  'grillCompatibility',
  'pairings',
  // Recipe
  'mealType',
  'cookingMethod',
  'holiday',
  'hardwood',
  'relatedCategories',
  // Learn
  'categories', 'tags', 'featuredContent',
];

type TFn = ReturnType<typeof useTranslation>['t'];

// Facets allowed per search type (undefined = all FACET_META keys allowed)
const ALLOWED_FACETS: Partial<Record<string, Array<string>>> = {
  Learn: ['categories', 'tags', 'featuredContent'],
};

// Build FilterConfig entries from API facets
export const buildFilterConfigs = (
  facets: Array<Facet>,
  searchType: string,
  t: TFn,
): Array<FilterConfig> => {
  const allowed = ALLOWED_FACETS[searchType];

  const sorted = [...facets].sort((a, b) => {
    const ai = FACET_ORDER.indexOf(a.facet);
    const bi = FACET_ORDER.indexOf(b.facet);
    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
  });

  return sorted
    .filter((f) => {
      const meta = FACET_META[f.facet];
      if (!meta || !f.pairs.length || (allowed && !allowed.includes(f.facet))) return false;
      // Toggle filters: only show when there are items with the 'true' value
      if (meta.toggle && !f.pairs.some((p) => p.value === 'true')) return false;
      return true;
    })
    .map((f) => {
      const meta = FACET_META[f.facet];
      const options: Array<FilterOption> = f.pairs.map((p) => ({
        value: p.value,
        label: f.facet === 'grillSeries' ? p.value.replace(/ Series$/i, '') : p.value,
        count: p.count,
      }));

      return {
        key: f.facet,
        label: t(meta.labelKey, undefined, { default: meta.labelDefault }),
        singleSelect: meta.singleSelect ?? SINGLE_VALUE_FILTER_KEYS.includes(f.facet),
        toggle: meta.toggle,
        options,
      };
    });
};
